// apps/web/src/features/clients/components/ExpandableText.tsx
//
// Requisites blocks run to hundreds of characters. Showing them in full turns a
// three-entity client into a wall of text, so the section shows a short preview
// and the operator expands the one they actually need (AC-5).
//
// INVARIANT: the preview is display-only. Copy actions always read the ORIGINAL
// string, never this truncation.
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../../ui/index.js';
import { useTouchActionStyle } from '../useMobileFieldStyle.js';

/** Lines kept in the collapsed preview. */
export const PREVIEW_LINES = 2;
/** Characters kept in the collapsed preview. */
export const PREVIEW_CHARS = 140;

export function buildPreview(text: string): { preview: string; truncated: boolean } {
  const lines = text.split('\n');
  const clippedByLine = lines.slice(0, PREVIEW_LINES).join('\n');
  const clipped = clippedByLine.slice(0, PREVIEW_CHARS);
  const truncated = clipped.length < text.length;
  return { preview: clipped, truncated };
}

export interface ExpandableTextProps {
  text: string;
  /** Accessible name for the expand control, naming the record it belongs to. */
  expandLabel: string;
}

export function ExpandableText({ text, expandLabel }: ExpandableTextProps) {
  const { t } = useTranslation(['clients']);
  const actionStyle = useTouchActionStyle();
  const [expanded, setExpanded] = useState(false);
  const bodyId = useId();
  const { preview, truncated } = buildPreview(text);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--s-2)', minWidth: 0 }}>
      <div
        id={bodyId}
        style={{
          whiteSpace: 'pre-wrap',
          overflowWrap: 'anywhere',
          fontSize: 'var(--fz-13)',
          color: 'var(--fg-2)',
        }}
      >
        {expanded || !truncated ? text : `${preview}…`}
      </div>
      {truncated && (
        <div>
          <Button
            size="sm"
            kind="quiet"
            style={actionStyle}
            aria-expanded={expanded}
            aria-controls={bodyId}
            aria-label={expandLabel}
            onClick={() => setExpanded((v) => !v)}
          >
            {expanded ? t('clients:actions.collapseText') : t('clients:actions.expandText')}
          </Button>
        </div>
      )}
    </div>
  );
}
